import { Stack } from '@mui/material'
import { DistributionSportChip } from '@/components/DistributionSportChip'
import type { Column, Filter } from '@/components/table/base'
import {
  distributionListSportLabelsMap,
  possibleRecipientTypes
} from '@/constants/distributionList'
import type { DistributionRecipientWithOperatorName } from '@/stores/distributionRecipients'

export const DISTRIBUTION_LIST_COLUMNS: Column<DistributionRecipientWithOperatorName>[] = [
  {
    id: 'emailAddress',
    label: 'Email',
    type: 'text',
    sortAttribute: 'emailAddress'
  },
  {
    id: 'sports',
    type: 'component',
    label: 'Sports',
    render: (sports) => (
      <Stack direction="row" gap={1} py={1} flexWrap="wrap">
        {(sports.length ? sports : ['ALL' as const]).sort().map((sport) => (
          <DistributionSportChip key={sport} sport={sport} />
        ))}
      </Stack>
    )
  },
  {
    id: 'recipientType',
    label: 'Recipient Type',
    type: 'text',
    sortAttribute: 'recipientType'
  },
  {
    id: '$operatorId',
    label: 'Operator ID',
    type: 'text',
    transform: (row) =>
      'operatorId' in row && row?.operatorId
        ? row?.operatorName
          ? `${row.operatorId} (${row.operatorName})`
          : `${row.operatorId}`
        : 'N/A'
  }
]

export const DISTRIBUTION_LIST_FILTERS: Filter<DistributionRecipientWithOperatorName>[] = [
  {
    type: 'freeTextSearch',
    paramName: 'ft',
    placeholder: 'Search',
    filterOptions: (row, searchText) => {
      const { operatorName, operatorId, emailAddress } = row

      return (
        emailAddress.toLowerCase().includes(searchText.toLowerCase()) ||
        (operatorName && operatorName.toLowerCase().includes(searchText.toLowerCase())) ||
        (operatorId && operatorId.toString().toLowerCase().includes(searchText.toLowerCase())) ||
        false
      )
    },
    width: 250
  },
  {
    type: 'select',
    paramName: 'recipientType',
    label: 'Recipient Type',
    options: possibleRecipientTypes.map((type) => ({ value: type })),
    filterOptions: (row, [selectedValue]) => {
      return row.recipientType === selectedValue.value
    },
    width: 150
  },
  {
    type: 'select',
    paramName: 'sport',
    label: 'Sport',
    multiple: true,
    options: Object.entries(distributionListSportLabelsMap)
      .map(([key, name]) => ({
        value: key,
        label: name
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    filterOptions(row, selectedOption) {
      if (row.sports.length === 0) {
        return true // Because ADMIN has no sports assigned but in fact they are assigned to all sports
      }
      const selectedValues = selectedOption.map((option) => option.value)
      return row.sports.some((sport) => selectedValues.includes(sport))
    }
  }
]
